/**
 * El último repaso de los botones antes de crear el proyecto (2026-10-29).
 *
 * El worker cablea cada botón al armar el sitio, pero el asistente llega
 * después: los datos de contacto se escriben en su paso 8 (la IA no tenía el
 * número de WhatsApp y dejó `https://wa.me/`), y cambiar la variante de una
 * sección la rehace desde el bloque, con sus `#` de muestra. En un consultorio
 * dental salieron cinco botones sin destino. Aquí, con los datos ya finales:
 *
 * - Un botón de canal («WhatsApp», «Llámanos», «Escríbenos», o que ya apunta a
 *   wa.me / tel: / mailto:) va a ese canal con el dato del negocio; sin dato,
 *   al contacto.
 * - «Ver en el mapa», «Cómo llegar»: a Google Maps con la dirección.
 * - Lo que no lleva a ningún sitio (`#`, vacío, wa.me sin número): los «Saber
 *   más» repetidos en las tarjetas de una sección se quitan (no dicen nada que
 *   la tarjeta no diga); lo demás va al formulario de la página o al contacto.
 *
 * No toca lo que se arma solo: botones de ofertas (`cmsLink`), formularios,
 * comportamientos ni tarjetas de contacto (`contactSlot`, ver `contactCards`).
 */
import type { ElementNode } from './types'

export type LinkContacts = { whatsapp: string; phone: string; email: string; address: string }

type Node = ElementNode & {
  interaction?: { action?: string; href?: string; target?: string; cmsLink?: unknown }
  form?: unknown
  behavior?: unknown
  cmsRepeat?: unknown
  catalog?: unknown
  contactSlot?: unknown
}

const VAGUE = /^(saber m[aá]s|ver m[aá]s|leer m[aá]s|m[aá]s informaci[oó]n|learn more|read more|see more|more info|details|detalles)$/i

const digits = (value: string) => value.replace(/\D/g, '')

const text = (node: Node) => (node.content ?? node.ariaLabel ?? '').replace(/<[^>]*>/g, '').trim()

type Channel = 'whatsapp' | 'call' | 'email' | 'map'

function channelOf(label: string, href: string): Channel | null {
  const lower = label.toLowerCase()
  if (/whats\s?app/.test(lower) || /wa\.me|api\.whatsapp/.test(href)) return 'whatsapp'
  if (/^tel:/i.test(href) || /\b(ll[aá]m|call\b|tel[eé]fono)/.test(lower)) return 'call'
  if (/^mailto:/i.test(href) || /\b(correo|e-?mail|escr[ií]benos)\b/.test(lower)) return 'email'
  if (/\b(mapa|map|c[oó]mo llegar|directions|get directions|ubicaci[oó]n)\b/.test(lower)) return 'map'
  return null
}

/** El destino de un canal con los datos del negocio, o `null` si no los hay. */
function channelHref(channel: Channel, contacts: LinkContacts): string | null {
  if (channel === 'whatsapp') {
    const number = digits(contacts.whatsapp) || digits(contacts.phone)
    return number.length >= 7 ? `https://wa.me/${number}` : null
  }
  if (channel === 'call') {
    const number = contacts.phone.trim() || contacts.whatsapp.trim()
    return digits(number).length >= 7 ? `tel:${number.replace(/[^\d+]/g, '')}` : null
  }
  if (channel === 'email') return /@/.test(contacts.email) ? `mailto:${contacts.email.trim()}` : null
  return contacts.address.trim() ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contacts.address.trim())}` : null
}

/** Si un enlace no lleva a ningún sitio. */
function isDead(href: string): boolean {
  if (!href || href === '#') return true
  if (/^https?:\/\/(wa\.me|api\.whatsapp\.com)\/?(\?.*)?$/i.test(href)) return true
  if (/^tel:\D*$/i.test(href)) return true
  if (/^mailto:\s*$/i.test(href)) return true
  return false
}

export type WireContext = {
  contacts: LinkContacts
  /** La página de contacto (`/contacto`), si la hay. */
  contactPath: string | null
}

/** Los botones de una página, con su destino. */
export function wireLinks(elements: ElementNode[], context: WireContext, pageSlug: string): ElementNode[] {
  /* En la página de contacto, lo que no lleva a nada va a SU formulario. */
  const formAnchor = (elements as Node[]).find((section) => section.anchorId && containsForm(section))?.anchorId
  const onContactPage = Boolean(context.contactPath && pageSlug === context.contactPath)
  const fallback = onContactPage && formAnchor ? `#${formAnchor}` : context.contactPath ?? (formAnchor ? `#${formAnchor}` : null)

  const deadVague = (node: Node): number => ((node.type === 'button' || node.type === 'link') && VAGUE.test(text(node)) && isDead(node.interaction?.href ?? node.href ?? '') ? 1 : 0)
    + (node.children ?? []).reduce((sum, child) => sum + deadVague(child as Node), 0)
  let dropVague = false

  const visit = (node: Node): Node | null => {
    if (node.form || node.behavior || node.cmsRepeat || node.catalog) return node
    if ((node.type === 'button' || node.type === 'link') && !node.interaction?.cmsLink && node.interaction?.action !== 'submit' && !node.contactSlot) {
      const href = node.interaction?.href ?? node.href ?? ''
      const label = text(node)
      const channel = channelOf(label, href)
      const wanted = channel ? channelHref(channel, context.contacts) : null
      if (wanted) return wanted === href ? node : withHref(node, wanted)
      if (isDead(href)) {
        if (dropVague && VAGUE.test(label)) return null
        return fallback ? withHref(node, fallback) : node
      }
      /* Un canal sin dato que apuntaba a otra página (el «WhatsApp» que fue al
         contacto) se queda donde está. */
      return node
    }
    if (!node.children?.length) return node
    const children = node.children.map((child) => visit(child as Node)).filter((child): child is Node => child !== null)
    const same = children.length === node.children.length && children.every((child, index) => child === node.children[index])
    return same ? node : { ...node, children }
  }
  return elements.map((section) => {
    dropVague = deadVague(section as Node) >= 2
    return visit(section as Node) ?? section
  })
}

function withHref(node: Node, href: string): Node {
  const external = /^https?:/i.test(href)
  return {
    ...node,
    ...(typeof node.href === 'string' ? { href } : {}),
    interaction: {
      ...node.interaction,
      action: href.startsWith('#') ? 'anchor' : 'link',
      href,
      ...(external ? { target: '_blank' } : {}),
    },
  }
}

function containsForm(node: Node): boolean {
  return (node.type as string) === 'form' || Boolean(node.form) || (node.children ?? []).some((child) => containsForm(child as Node))
}
